// Faceoff mini-mechanic — target-pick + timing commit.
//
// Owns:   the rule that turns each centre's "pick a teammate + press
//         Pass" sequence during the Faceoff phase into a puck pop at
//         the Faceoff → Play transition.
// Reads:  per-centre input.moveX / moveY / buttons during the Faceoff
//         window, state.players (for target teammate position lookup),
//         state.rules.faceoffIntents (carried across ticks).
// Mutates: state.rules.faceoffIntents (per-tick during Faceoff),
//          state.puck.vel / pos / mode (at Faceoff → Play).
// Emits:  no events directly. The post-resolution pop fires the usual
//         cascade (Loose → first stick claim → Controlled).
//
// Phase flow at a faceoff:
//   Faceoff (180 ticks) — each tick:
//     1. Sample the centre's stick → currentTarget (LW/RW/LD/RD or null)
//        - forward+left  → LW   forward+right → RW
//        - back+left     → LD   back+right    → RD
//     2. If currentTarget changed → reset targetSetAtTick = state.tick
//        (this is what gates the stability bonus build-up).
//     3. Edge-detect Pass press → if rising edge AND currentTarget !== null
//        AND no prior commit → latch commit { target, commitTick,
//        stabilityTicks }.
//   → Faceoff → Play (drop tick) — resolveFaceoffPop runs:
//     - Compute timing_score (peak at drop tick, linear falloff over
//       `faceoffTimingWindowTicks`) and stability_score (linear ramp
//       to 1.0 over `faceoffStabilityFullTicks`) for each team that
//       committed.
//     - total = timing + stability (range 0..2).
//     - Winner = higher total. Tie = RNG flip. No commit = participate
//       with score 0.
//     - Pop the puck from the dot toward the winner's targeted
//       teammate's current position; speed scales with margin.
//     - If neither team committed, tiny random contested pop.
//
// Zero-allocation.

import { InputButton, type PlayerInput, type TeamId } from "@arcade-hockey/shared";
import { nextFloat } from "../math/Random.js";
import { faceoffSpotInto } from "../state/faceoff-spots.js";
import { tuning } from "../state/tuning.js";
import type {
  FaceoffCommit,
  FaceoffIntentTracker,
  FaceoffTarget,
  MatchState,
  PlayerState,
} from "../state/types.js";

// Lateral and forward stick thresholds. Inputs land in [-1, 1]; the
// centre needs to push past the threshold to register a quadrant. The
// thresholds are deliberately loose so a small directional input is
// enough (per the user's spec: "even a small left amount").
const LATERAL_THRESHOLD = 0.2;
const FORWARD_THRESHOLD = 0.2;

// Speed range for the puck pop. Margin between winner & loser scaled
// in here. Min speed is the "contested" speed for tiny margins (puck
// dribbles near the dot); max speed is a clean uncontested win that
// reaches the targeted teammate before drag bleeds it out.
const POP_SPEED_MIN = 4.0;
const POP_SPEED_MAX = 12.0;

const DOT_SCRATCH = { x: 0, y: 0 };

/**
 * Map a stick input to one of the four target quadrants from a team's
 * POV. `attackDir` is +1 for HOME, -1 for AWAY.
 *
 * Returns `null` if the stick isn't clearly in a quadrant — both axes
 * must exceed their respective thresholds. A neutral stick or a pure
 * forward / back (no lateral component) doesn't register a target.
 */
export function targetFromInput(input: PlayerInput, attackDir: 1 | -1): FaceoffTarget | null {
  const forward = input.moveX * attackDir;
  const lateral = input.moveY; // +y is "left" by convention (LW/LD spawn at +y)
  if (Math.abs(forward) < FORWARD_THRESHOLD) return null;
  if (Math.abs(lateral) < LATERAL_THRESHOLD) return null;
  const isForward = forward > 0;
  const isLeft = lateral > 0;
  if (isForward && isLeft) return "LW";
  if (isForward && !isLeft) return "RW";
  if (!isForward && isLeft) return "LD";
  return "RD";
}

function lazyTracker(state: MatchState, team: "home" | "away"): FaceoffIntentTracker {
  if (state.rules.faceoffIntents === undefined) state.rules.faceoffIntents = {};
  const existing = state.rules.faceoffIntents[team];
  if (existing !== undefined) return existing;
  const t: FaceoffIntentTracker = {
    currentTarget: null,
    targetSetAtTick: state.tick,
    passHeldLastTick: false,
    commit: null,
  };
  state.rules.faceoffIntents[team] = t;
  return t;
}

/**
 * Per-tick faceoff input pump. Runs during the Faceoff phase from
 * `core/SimTick.ts` BEFORE the Step 5 input pass.
 *
 * For each centre input:
 *   - Updates the team's `currentTarget` from the stick quadrant.
 *   - Resets `targetSetAtTick` if the target changed.
 *   - Edge-detects Pass to latch the team's commit (irrevocable for
 *     the rest of the window).
 */
export function collectFaceoffIntents(state: MatchState, inputs: ReadonlyArray<PlayerInput>): void {
  if (state.clock.phase !== "Faceoff") return;
  for (const input of inputs) {
    const player = input.playerId !== null ? state.playersById.get(input.playerId) : null;
    if (!player) continue;
    if (player.position !== "C") continue;

    const team: "home" | "away" = player.teamId;
    const tracker = lazyTracker(state, team);

    // If already committed, stop accepting changes for this team.
    if (tracker.commit !== null) {
      // Still need to update passHeldLastTick for edge detection consistency
      tracker.passHeldLastTick = (input.buttons & InputButton.Pass) !== 0;
      continue;
    }

    const attackDir: 1 | -1 = team === "home" ? 1 : -1;
    const newTarget = targetFromInput(input, attackDir);

    // Update stability tracking. Note: going FROM a target TO null
    // doesn't reset (the player just released the stick); only a
    // change between two non-null targets does. This rewards smoothly
    // holding the target while letting the player ease off the stick.
    if (newTarget !== null && newTarget !== tracker.currentTarget) {
      tracker.currentTarget = newTarget;
      tracker.targetSetAtTick = state.tick;
    }

    // Edge-detect Pass press → latch commit.
    const passHeld = (input.buttons & InputButton.Pass) !== 0;
    if (passHeld && !tracker.passHeldLastTick && tracker.currentTarget !== null) {
      const stability = state.tick - tracker.targetSetAtTick;
      tracker.commit = {
        target: tracker.currentTarget,
        commitTick: state.tick,
        stabilityTicks: stability,
      };
    }
    tracker.passHeldLastTick = passHeld;
  }
}

/**
 * Resolve the collected commits into a puck pop at the Faceoff → Play
 * transition. Called once from `core/SimTick.ts` when the Faceoff
 * timer hits 0, BEFORE the phase flip to Play. Sets the puck velocity
 * (mode stays Loose — first stick to reach it claims it).
 *
 * Scoring:
 *   - timing_score = max(0, 1 - |commitTick - dropTick| / timingWindow)
 *   - stability_score = min(1, stabilityTicks / stabilityFullTicks)
 *   - total = timing + stability (0..2)
 *   - Winner = team with higher total. Tie → RNG flip.
 *   - margin = winner_total - loser_total (in [0, 2])
 *   - pop_speed = lerp(POP_SPEED_MIN, POP_SPEED_MAX, margin / 2)
 *
 * Pop direction = (winner's targeted teammate.pos - puck.pos), normalised.
 */
export function resolveFaceoffPop(state: MatchState): void {
  // The drop tick is the current tick — Faceoff is about to flip to
  // Play. A commit at this tick scores 1.0 on timing.
  const dropTick = state.tick;
  const spot = state.rules.nextFaceoffSpot ?? "Center";
  faceoffSpotInto(DOT_SCRATCH, spot);
  state.puck.pos.x = DOT_SCRATCH.x;
  state.puck.pos.y = DOT_SCRATCH.y;
  state.puck.mode = "Loose";
  state.puck.ownerId = null;
  state.puck.noRecaptureUntilTick = state.tick;
  state.puck.lastTouchedBy = null;
  // Centre-lockout — both centres are "tied up in the draw" for the
  // next ~0.5s so the puck actually reaches the targeted teammate
  // rather than being snagged by whichever centre is closest to the
  // dot.
  state.puck.faceoffCenterLockoutUntilTick = state.tick + tuning.puck.faceoffCenterLockoutTicks;

  const intents = state.rules.faceoffIntents;
  const homeCommit = intents?.home?.commit ?? null;
  const awayCommit = intents?.away?.commit ?? null;
  const homeScore = scoreCommit(homeCommit, dropTick);
  const awayScore = scoreCommit(awayCommit, dropTick);

  if (homeCommit === null && awayCommit === null) {
    // Neither team committed — small contested random pop.
    contestedRandomPop(state);
    state.rules.faceoffIntents = undefined;
    return;
  }

  // Decide winner. Tie-break with the RNG so the result is
  // deterministic on a given seed. The `homeCommit !== null` check
  // both flows from the score gates above (home wins → must be
  // committed) and narrows the type for assignment.
  let winnerTeam: TeamId;
  let winnerCommit: FaceoffCommit;
  let margin: number;
  if (homeScore > awayScore && homeCommit !== null) {
    winnerTeam = "home";
    winnerCommit = homeCommit;
    margin = homeScore - awayScore;
  } else if (awayScore > homeScore && awayCommit !== null) {
    winnerTeam = "away";
    winnerCommit = awayCommit;
    margin = awayScore - homeScore;
  } else if (homeCommit !== null && awayCommit !== null) {
    const flip = nextFloat(state.rng) >= 0.5;
    winnerTeam = flip ? "home" : "away";
    winnerCommit = flip ? homeCommit : awayCommit;
    margin = 0;
  } else if (homeCommit !== null) {
    winnerTeam = "home";
    winnerCommit = homeCommit;
    margin = homeScore - awayScore;
  } else if (awayCommit !== null) {
    winnerTeam = "away";
    winnerCommit = awayCommit;
    margin = awayScore - homeScore;
  } else {
    // Already handled above (both null → contestedRandomPop), but TS
    // can't prove it; fall through to a safe no-op pop.
    contestedRandomPop(state);
    state.rules.faceoffIntents = undefined;
    return;
  }

  popTowardTarget(state, winnerTeam, winnerCommit.target, margin);
  state.rules.faceoffIntents = undefined;
}

function scoreCommit(commit: FaceoffCommit | null, dropTick: number): number {
  if (commit === null) return 0;
  const timingWindow = tuning.clock.faceoffTimingWindowTicks;
  const timing = Math.max(0, 1 - Math.abs(commit.commitTick - dropTick) / timingWindow);
  const stabilityFull = tuning.clock.faceoffStabilityFullTicks;
  const stability = Math.min(1, commit.stabilityTicks / stabilityFull);
  return timing + stability;
}

function popTowardTarget(
  state: MatchState,
  team: TeamId,
  target: FaceoffTarget,
  margin: number,
): void {
  const targetPlayer = findTeamPlayer(state, team, target);
  const speed = POP_SPEED_MIN + (margin / 2) * (POP_SPEED_MAX - POP_SPEED_MIN);
  if (targetPlayer === null) {
    // Targeted role somehow missing (shouldn't happen at standard 5v5).
    // Fall back to a small forward pop.
    const dir = team === "home" ? 1 : -1;
    state.puck.vel.x = dir * speed;
    state.puck.vel.y = 0;
    return;
  }
  const dx = targetPlayer.pos.x - state.puck.pos.x;
  const dy = targetPlayer.pos.y - state.puck.pos.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-6) {
    state.puck.vel.x = 0;
    state.puck.vel.y = 0;
    return;
  }
  state.puck.vel.x = (dx / len) * speed;
  state.puck.vel.y = (dy / len) * speed;
}

function contestedRandomPop(state: MatchState): void {
  const r = nextFloat(state.rng);
  // Small pop in a random cardinal direction. Speed sits at the
  // contested-floor.
  const dir = Math.floor(r * 4);
  let dx = 0;
  let dy = 0;
  if (dir === 0) dx = 1;
  else if (dir === 1) dx = -1;
  else if (dir === 2) dy = 1;
  else dy = -1;
  state.puck.vel.x = dx * POP_SPEED_MIN;
  state.puck.vel.y = dy * POP_SPEED_MIN;
}

function findTeamPlayer(state: MatchState, team: TeamId, role: FaceoffTarget): PlayerState | null {
  for (const p of state.players) {
    if (p.teamId !== team) continue;
    if (p.position !== role) continue;
    return p;
  }
  return null;
}

// Hash-mapping helpers for `core/SimTick.ts:hashRulesInto`.
export function faceoffTargetToInt(t: FaceoffTarget | null | undefined): number {
  if (t === null || t === undefined) return 0;
  switch (t) {
    case "LW":
      return 1;
    case "RW":
      return 2;
    case "LD":
      return 3;
    case "RD":
      return 4;
  }
}
